'use es6';
/**
 * Simple type containing properties relevant to a LayoutWidget's context.
 * @type {Object}
 */

const WidgetContext = class {
  constructor(props = {}) {
    this.rowId = props.rowId || null;
    this.parentId = props.parentId || null;
  }

};
/**
 * Some implementation for recursing normalized layoutData.
 *
 * @param {Object}          widgets     Normalized immutable widgets.
 * @param {Object}          rows        Normalized immutable rows.
 * @param {String}          widgetId    Id of widget to begin recursing from.
 * @param {Function}        callback    Function to be invoked for each widget.
 * @param  {WidgetContext}  __context
 * @return {Boolean}
 */

function some(widgets, rows, widgetId, callback, __context) {
  const widget = widgets[widgetId];

  if (callback(widget, __context || new WidgetContext()) === true) {
    return true;
  }

  if (!widget.rows) {
    return false;
  }

  return widget.rows.some(rowId => {
    const row = rows[rowId];
    const context = new WidgetContext({
      rowId,
      parentId: widgetId
    });
    return row.some(__widgetId => some(widgets, rows, __widgetId, callback, context));
  });
}
/**
 * Recurses a normalized layout of `widgets` and `rows` collections.
 * The recursion is top-down from the passed `widgetId`.
 *
 * @param {Object} widgets        Normalized immutable widgets.
 * @param {Object} rows           Normalized immutable rows.
 * @param {String} widgetId       Id of widget to begin recursing from.
 * @param {Function} callback     Function to be invoked for each widget.
 */


export function forEachNormalizedWidgets(widgets, rows, widgetId, callback) {
  const cb = (widget, context) => {
    callback(widget, context);
    return false;
  };

  some(widgets, rows, widgetId, cb);
}
/**
 * Recurses a normalized layout of `widgets` and `rows` collections, exiting when `callback(widget) === true`.
 * The recursion is top-down from the passed `widgetId`.
 *
 * @param {Object} widgets        Normalized immutable widgets.
 * @param {Object} rows           Normalized immutable rows.
 * @param {String} widgetId       Id of widget to begin recursing from.
 * @param {Function} callback     Function to be invoked for each widget.
 * @return {Boolean} True when `callback` first return true, false otherwise.
 */

export function someNormalizedWidgets(widgets, rows, widgetId, callback) {
  return some(widgets, rows, widgetId, callback);
}